import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, Loader2, Timer, CheckCircle2, XCircle, Upload,
  ChevronLeft, ChevronRight, Home, Eye, RotateCcw,
  AlertTriangle, Trash2, Send, BookOpen, Globe, Clock,
  Zap, TrendingUp, Medal, Sparkles, Lock, Trophy, Target,
  Play, FileText, PauseCircle, LogOut, X, Download
} from "lucide-react";
import { toast } from "sonner";
import MathText from "@/components/MathText";
import DescriptiveFeedback from "@/components/DescriptiveFeedback";
import IdealAnswerDisplay from "@/components/IdealAnswerDisplay";
import { downloadQuestionSheet } from "@/utils/downloadSheet";

const API = `${window.location.origin}/api`;

const NON_TEACHING_PATTERNS = {
  librarian: ["kvs", "emrs"], library_science: ["kvs", "emrs"],
  nurse: ["emrs"], staff_nurse: ["emrs"],
  lab_assistant: ["emrs"], jsa: ["kvs"], hostel_warden: ["emrs"],
};

export default function MockTest({ user, subjectSelection }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const getExamType = () => {
    const cat = subjectSelection?.category;
    if (cat === "kvs_prt" || cat === "ctet" || cat === "prt") return "prt";
    if (cat === "pgt") return "pgt";
    if (cat === "non_teaching") return "non_teaching";
    return "tgt";
  };
  const examType = getExamType();
  // PRT Full Mock always uses ALL subjects combined (like real KVS PRT exam)
  const [subject, setSubject] = useState(examType === "prt" ? "all" : (subjectSelection?.subject === "all" ? "" : (subjectSelection?.subject || "")));
  const [medium, setMedium] = useState(subjectSelection?.language === "hindi" ? "hi" : "en");
  const needsSubjectPick = examType !== "prt" && subjectSelection?.subject === "all";
  // PRT always goes to mock_list directly (all subjects, KVS pattern only)
  const [step, setStep] = useState(needsSubjectPick ? "subject_pick" : (examType === "prt" ? "mock_list" : "pattern_select"));
  const [config, setConfig] = useState(null);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [availableMocks, setAvailableMocks] = useState([]);
  const [pausedMocks, setPausedMocks] = useState({});
  const [selectedMock, setSelectedMock] = useState(null);
  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Leaderboard state
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [generatingMockNum, setGeneratingMockNum] = useState(null);
  const [genProgress, setGenProgress] = useState({ step: "", objDone: 0, objTotal: 0, descDone: 0, descTotal: 0 });
  const pollRef = useRef(null);

  // Test state
  const [currentQ, setCurrentQ] = useState(0);
  const [objAnswers, setObjAnswers] = useState({});
  const [descUploads, setDescUploads] = useState({});
  const [extractedTexts, setExtractedTexts] = useState({});
  const [ocrLoading, setOcrLoading] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // Result state
  const [result, setResult] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [solutions, setSolutions] = useState(null);
  
  // Direct link handling
  const [directMockId, setDirectMockId] = useState(null);
  const [autoStartMock, setAutoStartMock] = useState(null);

  const fetchLeaderboard = async () => {
    if (!result || !examType || !selectedPattern || !subject || !selectedMock) return;
    
    setLoadingLeaderboard(true);
    try {
      const response = await fetch(`${API}/mock-tests/leaderboard?exam_type=${examType}&exam_pattern=${selectedPattern}&subject=${subject}&mock_number=${selectedMock.mock_number}&limit=50`);
      const data = await response.json();
      
      if (data.success) {
        setLeaderboardData(data);
        setShowLeaderboard(true);
      }
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
      toast.error("Failed to load leaderboard");
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  useEffect(() => {
    fetch(`${API}/mock-tests/config`).then(r => r.json()).then(setConfig).catch(() => {});
  }, []);
  
  // Handle direct link from email
  useEffect(() => {
    const directParam = searchParams.get('direct');
    if (directParam && user) {
      // Parse mock_id: "tgt_kvs_mathematics_1"
      const parts = directParam.split('_');
      if (parts.length >= 4) {
        const mockNum = parseInt(parts[parts.length - 1]);
        const subj = parts.slice(2, -1).join('_');
        const pattern = parts[1];
        const exam = parts[0];
        
        console.log('Direct link detected:', { exam, pattern, subj, mockNum });
        
        setDirectMockId(directParam);
        setSubject(subj);
        setSelectedPattern(pattern);
        setStep("mock_list");
        
        // Fetch available mocks and auto-start if ready
        fetchAvailableAndAutoStart(pattern, subj, exam, mockNum);
      }
    } else if (directParam && !user) {
      // Not logged in, redirect to login with return URL
      const returnUrl = `/mock-test?direct=${directParam}`;
      toast.info("Please login to attempt this mock test");
      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
    }
  }, [searchParams, user]);

  const fetchAvailableAndAutoStart = async (pattern, subj, exam, mockNum) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/mock-tests/available?exam_type=${exam}&exam_pattern=${pattern}&subject=${subj}&user_id=${user.id}`);
      const data = await res.json();
      setAvailableMocks(data.mocks || []);
      
      // Check if the specific mock is ready
      const targetMock = (data.mocks || []).find(m => m.mock_number === mockNum);
      if (targetMock) {
        console.log('Target mock found and ready:', targetMock);
        setAutoStartMock(targetMock);
        toast.success(`Mock Test ${mockNum} is ready! Click "Start Test" to begin.`);
      } else {
        toast.info(`Mock Test ${mockNum} is not yet ready. Please generate it first.`);
      }
      
      // Also check for generation in progress
      if (data.generation_in_progress) {
        const genMock = data.generation_in_progress.generating_mock || null;
        const currentStep = data.generation_in_progress.current_step || "";
        
        setGenerating(true);
        setGeneratingMockNum(genMock);
        parseProgress(currentStep, {
          objDone: data.generation_in_progress.obj_done || 0,
          objTotal: data.generation_in_progress.obj_total || 0,
          descDone: data.generation_in_progress.desc_done || 0,
          descTotal: data.generation_in_progress.desc_total || 0,
        });
        startPolling(pattern);
      }
    } catch (e) {
      console.error("Failed to load mock tests:", e);
      toast.error("Failed to load mock tests");
    }
    setLoading(false);
  };

  useEffect(() => {
    // PRT always uses "all" subjects combined — skip pattern selection, go directly to mock list
    if (config && examType === "prt") {
      setSelectedPattern("kvs");
      setStep("mock_list");
      fetchAvailable("kvs");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, examType]);

  useEffect(() => {
    if (config && examType === "non_teaching" && subject) {
      const subKey = subject.toLowerCase().replace(/ /g, "_");
      const patterns = NON_TEACHING_PATTERNS[subKey];
      if (patterns && patterns.length === 1) {
        setSelectedPattern(patterns[0]);
        setStep("mock_list");
        fetchAvailable(patterns[0]);
      }
    }
  }, [config, examType, subject]);

  // Timer
  useEffect(() => {
    if (step !== "test_active" || timeLeft <= 0) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); handleSubmit(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [step, timeLeft > 0]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const getAvailablePatterns = () => {
    if (examType === "non_teaching" && subject) {
      const subKey = subject.toLowerCase().replace(/ /g, "_");
      return NON_TEACHING_PATTERNS[subKey] || ["kvs", "emrs"];
    }
    return config?.level_config?.[examType]?.patterns || [];
  };

  const fetchAvailable = async (pattern, subjectOverride) => {
    setLoading(true);
    try {
      const subj = subjectOverride || subject || "all";
      const res = await fetch(`${API}/mock-tests/available?exam_type=${examType}&exam_pattern=${pattern}&subject=${subj}&user_id=${user.id}`);
      const data = await res.json();
      setAvailableMocks(data.mocks || []);
      setPausedMocks(data.paused_mocks || {});
      
      // Restore generation state from backend (for persistence across page reloads)
      if (data.generation_in_progress) {
        const genMock = data.generation_in_progress.generating_mock || null;
        const currentStep = data.generation_in_progress.current_step || "";
        
        setGenerating(true);
        setGeneratingMockNum(genMock);
        parseProgress(currentStep, {
          objDone: data.generation_in_progress.obj_done || 0,
          objTotal: data.generation_in_progress.obj_total || 0,
          descDone: data.generation_in_progress.desc_done || 0,
          descTotal: data.generation_in_progress.desc_total || 0,
        });
        
        // Start polling to keep progress updated
        startPolling(pattern);
        
        console.log(`Restored generation state: Mock #${genMock} is generating`);
      } else {
        // No generation in progress, clear states
        setGenerating(false);
        setGeneratingMockNum(null);
      }
    } catch (e) { 
      console.error("Failed to load mock tests:", e);
      toast.error("Failed to load mock tests"); 
    }
    setLoading(false);
  };

  const parseProgress = (stepStr, extra = {}) => {
    // Parse "loading_questions|10|60" format
    const parts = stepStr.split("|");
    const phase = parts[0] || "";
    const done = parseInt(parts[1]) || extra.objDone || 0;
    const total = parseInt(parts[2]) || extra.objTotal || 0;
    setGenProgress({
      step: phase,
      objDone: extra.objDone ?? done,
      objTotal: extra.objTotal ?? total,
      descDone: extra.descDone ?? 0,
      descTotal: extra.descTotal ?? 0,
    });
  };

  const handlePatternSelect = (pattern) => {
    setSelectedPattern(pattern);
    setStep("mock_list");
    fetchAvailable(pattern);
  };

  const handleLoadMock = async (mockNum) => {
    // Prevent starting another generation while one is in progress
    if (generating && generatingMockNum && generatingMockNum !== mockNum) {
      toast.error(`Mock ${generatingMockNum} is still being generated. Please wait for it to finish first.`);
      return;
    }
    setGenerating(true);
    setGeneratingMockNum(mockNum);
    try {
      const subj = subject || "all";
      const res = await fetch(`${API}/mock-tests/student-generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, exam_type: examType, exam_pattern: selectedPattern, subject: subj, mock_number: mockNum }),
      });
      const data = await res.json();
      if (data.status === "ready") {
        setGenerating(false);
        setGeneratingMockNum(null);
        toast.success(data.message || "Mock test is ready!");
        fetchAvailable(selectedPattern);
        return;
      }
      startPolling(selectedPattern);
    } catch (e) { setGenerating(false); setGeneratingMockNum(null); toast.error("Failed: " + e.message); }
  };

  const startPolling = (pattern) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const subj = subject || "all";
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API}/mock-tests/student-progress?exam_type=${examType}&exam_pattern=${pattern}&subject=${subj}`);
        const data = await res.json();
        
        console.log("Poll response:", data); // Debug log
        
        parseProgress(data.current_step || "", {
          objDone: data.obj_done || 0, 
          objTotal: data.obj_total || 0,
          descDone: data.desc_done || 0, 
          descTotal: data.desc_total || 0,
        });
        
        if (data.status === "ready") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          setGeneratingMockNum(null);
          toast.success("Mock test ready!");
          fetchAvailable(pattern);
        } else if (data.status === "doing_qc") {
          // Questions loaded, auto-QC running — keep showing progress, update step
          parseProgress("doing_qc", {});
          // Continue polling — QC will complete and step will become "Complete"
        } else if (data.status === "error") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          setGeneratingMockNum(null);
          toast.error("Generation failed. Please try again.");
        }
      } catch { /* ignore */ }
    }, 6000);
  };

  const saveProgressRef = useRef(null);

  // Auto-save progress to server (debounced, every 5s max)
  const saveProgress = useRef(null);
  const doSaveProgress = async (testId, answers, descAnswers, qIdx, remaining) => {
    if (!user?.id || !testId) return;
    try {
      await fetch(`${API}/mock-tests/${testId}/save-progress`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          objective_answers: answers,
          descriptive_answers: descAnswers,
          extracted_texts: extractedTexts,
          current_question: qIdx,
          time_left: remaining,
        }),
      });
    } catch { /* silent - best effort save */ }
  };

  // Schedule save when answers change
  useEffect(() => {
    if (step !== "test_active" || !testData?.id) return;
    if (saveProgressRef.current) clearTimeout(saveProgressRef.current);
    saveProgressRef.current = setTimeout(() => {
      doSaveProgress(testData.id, objAnswers, descUploads, currentQ, timeLeft);
    }, 3000);
    return () => { if (saveProgressRef.current) clearTimeout(saveProgressRef.current); };
  }, [objAnswers, descUploads, currentQ, step, testData?.id]);

  // Also save every 30s while test is active (for timer updates)
  useEffect(() => {
    if (step !== "test_active" || !testData?.id) return;
    const interval = setInterval(() => {
      doSaveProgress(testData.id, objAnswers, descUploads, currentQ, timeLeft);
    }, 30000);
    return () => clearInterval(interval);
  }, [step, testData?.id]);

  const handleStartTest = async (mock) => {
    setLoading(true);
    try {
      // Check for saved progress first
      const progressRes = await fetch(`${API}/mock-tests/${mock.id}/get-progress/${user.id}`);
      const progressData = await progressRes.json();

      const res = await fetch(`${API}/mock-tests/${mock.id}?medium=${medium}`);
      if (!res.ok) throw new Error("Failed to load test");
      const data = await res.json();

      setTestData(data);
      setSelectedMock(mock);

      if (progressData.found && progressData.time_left > 60) {
        // Resume from saved progress
        setObjAnswers(progressData.objective_answers || {});
        setDescUploads(progressData.descriptive_answers || {});
        setExtractedTexts(progressData.extracted_texts || {});
        setCurrentQ(progressData.current_question || 0);
        setTimeLeft(progressData.time_left);
        const answered = Object.keys(progressData.objective_answers || {}).length;
        toast.success(`Progress restored! ${answered} answers recovered. Resuming...`);
      } else {
        // Fresh start - clear any old progress
        setObjAnswers({});
        setDescUploads({});
        setExtractedTexts({});
        setCurrentQ(0);
        setTimeLeft(data.duration_minutes * 60);
        fetch(`${API}/mock-tests/${mock.id}/clear-progress/${user.id}`, { method: "DELETE" }).catch(() => {});
      }
      setStep("test_active");
    } catch { toast.error("Failed to start test"); }
    setLoading(false);
  };

  const handleOptionSelect = (qNum, optIdx) => setObjAnswers(prev => ({ ...prev, [String(qNum)]: optIdx }));

  const handleImageUpload = async (qNum, fileIdx, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(`${API}/firebase/upload`, { method: "POST", body: formData });
      const data = await res.json();
      if (data.success || data.url) {
        const url = data.url || data.public_url;
        setDescUploads(prev => {
          const curr = prev[String(qNum)] || [];
          const updated = [...curr];
          updated[fileIdx] = url;
          return { ...prev, [String(qNum)]: updated };
        });
        toast.success("Image uploaded — extracting text...");
        // Auto-trigger OCR
        const ocrKey = `${qNum}_${fileIdx}`;
        setOcrLoading(prev => ({ ...prev, [ocrKey]: true }));
        try {
          const ocrRes = await fetch(`${API}/mock-tests/ocr-preview`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              image_url: url,
              user_id: user?.id || "",
              test_id: testData?.id || "",
              q_num: String(qNum),
              img_idx: fileIdx,
            }),
          });
          const ocrData = await ocrRes.json();
          if (ocrData.success && ocrData.extracted_text) {
            setExtractedTexts(prev => ({ ...prev, [ocrKey]: ocrData.extracted_text }));
            toast.success("Text extracted successfully!");
          } else {
            setExtractedTexts(prev => ({ ...prev, [ocrKey]: "" }));
            toast.warning("Could not extract text. Try a clearer photo.");
          }
        } catch {
          setExtractedTexts(prev => ({ ...prev, [ocrKey]: "" }));
        }
        setOcrLoading(prev => ({ ...prev, [ocrKey]: false }));
      }
    } catch { toast.error("Upload failed"); }
  };

  const handleRemoveImage = (qNum, fileIdx) => {
    setDescUploads(prev => {
      const curr = [...(prev[String(qNum)] || [])];
      curr[fileIdx] = null;
      return { ...prev, [String(qNum)]: curr };
    });
    // Clear extracted text for this slot
    setExtractedTexts(prev => {
      const key = `${qNum}_${fileIdx}`;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleOcrPreview = async (qNum, fileIdx) => {
    const url = (descUploads[String(qNum)] || [])[fileIdx];
    if (!url) return;
    const key = `${qNum}_${fileIdx}`;
    setOcrLoading(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${API}/mock-tests/ocr-preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          image_url: url,
          user_id: user?.id || "",
          test_id: testData?.id || "",
          q_num: String(qNum),
          img_idx: fileIdx,
        }),
      });
      const data = await res.json();
      if (data.success && data.extracted_text) {
        setExtractedTexts(prev => ({ ...prev, [key]: data.extracted_text }));
        toast.success("Text extracted successfully!");
      } else if (data.status === "empty") {
        setExtractedTexts(prev => ({ ...prev, [key]: "[No text could be extracted. Try uploading a clearer image.]" }));
        toast.warning("No text found in image. Try a clearer photo.");
      } else {
        toast.error("OCR failed. Try again or upload a clearer image.");
      }
    } catch {
      toast.error("Failed to extract text. Check your connection.");
    }
    setOcrLoading(prev => ({ ...prev, [key]: false }));
  };

  const handlePause = async () => {
    clearInterval(timerRef.current);
    if (saveProgressRef.current) clearTimeout(saveProgressRef.current);
    // Save progress immediately before pausing
    await doSaveProgress(testData.id, objAnswers, descUploads, currentQ, timeLeft);
    toast.success("Progress saved! Resume anytime from the mock list.");
    setStep("mock_list");
  };

  const handleExit = async () => {
    clearInterval(timerRef.current);
    if (saveProgressRef.current) clearTimeout(saveProgressRef.current);
    await doSaveProgress(testData.id, objAnswers, descUploads, currentQ, timeLeft);
    setShowExitConfirm(false);
    navigate("/dashboard");
  };

  const handleSubmit = async () => {
    clearInterval(timerRef.current);
    if (saveProgressRef.current) clearTimeout(saveProgressRef.current);
    setStep("submitting");
    try {
      const cleanDesc = {};
      for (const [k, v] of Object.entries(descUploads)) {
        const urls = (v || []).filter(Boolean);
        if (urls.length > 0) cleanDesc[k] = urls;
      }
      const elapsed = testData ? testData.duration_minutes * 60 - timeLeft : 0;
      const res = await fetch(`${API}/mock-tests/${testData.id}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, objective_answers: objAnswers, descriptive_answers: cleanDesc, extracted_texts: extractedTexts, time_taken_seconds: elapsed }),
      });
      const data = await res.json();
      if (!data.success) { toast.error("Submission failed. Please try again."); setStep("test_active"); return; }

      const subId = data.submission_id;
      setSubmissionId(subId);

      // If already evaluated (no descriptive answers), show result immediately
      if (data.status === "evaluated" && data.result) {
        setResult(data.result); setStep("result"); return;
      }

      // Poll for evaluation to complete (background processing)
      let attempts = 0;
      const maxAttempts = 60; // poll up to 5 minutes (5s interval)
      const poll = async () => {
        try {
          const pollRes = await fetch(`${API}/mock-tests/submission/${subId}/status`);
          const pollData = await pollRes.json();
          if (pollData.status === "evaluated" && pollData.result) {
            setResult(pollData.result); setStep("result");
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, 5000);
          } else {
            toast.error("Evaluation timed out. Please check your results later.");
            setStep("mock_list");
          }
        } catch {
          if (attempts < maxAttempts) { attempts++; setTimeout(poll, 5000); }
        }
      };
      setTimeout(poll, 3000); // first poll after 3s
    } catch (e) { toast.error("Submission failed. Please check your connection."); setStep("test_active"); }
  };

  const handleShowSolutions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/mock-tests/submission/${submissionId}/solutions`);
      const data = await res.json();
      setSolutions(data.solutions || []); setStep("solutions");
    } catch { toast.error("Failed to load solutions"); }
    setLoading(false);
  };

  const handleViewSolutionsFromList = async (mock) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/mock-tests/solutions-by-mock/${mock.id}/${user.id}`);
      if (!res.ok) throw new Error("No solutions found");
      const data = await res.json();
      setSolutions(data.solutions || []);
      setResult(data.result || null);
      setSubmissionId(data.submission_id || null);
      setSelectedMock(mock);
      setStep("solutions");
    } catch { toast.error("No solutions found. Attempt the test first."); }
    setLoading(false);
  };

  const handleReattempt = () => {
    setStep("mock_list"); setResult(null); setSubmissionId(null); setSolutions(null); setTestData(null);
    fetchAvailable(selectedPattern);
  };

  const toggleMedium = () => setMedium(prev => prev === "en" ? "hi" : "en");

  const questions = testData?.questions || [];
  const currentQuestion = questions[currentQ];

  // ─── Render Match-the-Following Table ──────────────────────────────
  const renderMatchTable = (text) => {
    // Parse markdown table format: | Col1 | Col2 |\n|---|---|\n| data | data |
    const lines = text.split("\n");
    const tableLines = [];
    let beforeTable = [];
    let afterTable = [];
    let inTable = false;
    for (const line of lines) {
      if (line.trim().startsWith("|")) {
        inTable = true;
        if (!line.trim().match(/^\|[\s-|]+\|$/)) { // skip separator line
          tableLines.push(line);
        }
      } else if (inTable) {
        afterTable.push(line);
      } else {
        beforeTable.push(line);
      }
    }
    if (tableLines.length < 2) return null; // No valid table

    const parseRow = (line) => line.split("|").map(c => c.trim()).filter(Boolean);
    const header = parseRow(tableLines[0]);
    const rows = tableLines.slice(1).map(parseRow);

    return (
      <div>
        {beforeTable.length > 0 && <p className="mb-3 text-slate-200 whitespace-pre-wrap">{beforeTable.join("\n")}</p>}
        <div className="overflow-x-auto my-3">
          <table className="w-full border-collapse text-sm" data-testid="match-table">
            <thead>
              <tr>
                {header.map((h, i) => (
                  <th key={i} className="border border-slate-600 bg-slate-800 px-4 py-2 text-left text-blue-300 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? "bg-slate-900/50" : "bg-slate-800/30"}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="border border-slate-700 px-4 py-2 text-slate-300">
                      <MathText text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {afterTable.length > 0 && <p className="text-slate-300 whitespace-pre-wrap">{afterTable.join("\n")}</p>}
      </div>
    );
  };

  // Render question text with MathText + table support
  const renderQuestionText = (q) => {
    const text = medium === "hi" ? (q.question_hi || q.question_en) : q.question_en;
    if (!text) return null;
    // Check if it has a markdown table
    if (text.includes("|") && text.includes("---")) {
      const table = renderMatchTable(text);
      if (table) return table;
    }
    return <MathText text={text} className="whitespace-pre-wrap" />;
  };

  const subjectsList = {
    prt: ["hindi", "english", "mathematics", "evs", "general_knowledge", "pedagogy", "child_development"],
    tgt: ["hindi", "english", "mathematics", "science", "social_science", "computer_science", "physical_education"],
    pgt: ["hindi", "english", "mathematics", "physics", "chemistry", "biology", "commerce", "economics", "geography", "history", "computer_science"],
    non_teaching: ["librarian", "nurse", "lab_assistant", "jsa", "hostel_warden"],
  };

  // ─── Subject Picker ────────────────────────────────────────────────
  if (step === "subject_pick") {
    return (
      <div className="min-h-screen bg-slate-950 text-white p-4">
        <div className="max-w-lg mx-auto pt-6">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6" data-testid="mock-back-btn">
            <ArrowLeft className="w-5 h-5" /> Dashboard
          </button>
          <h1 className="text-2xl font-bold mb-2">Full Mock Test</h1>
          <p className="text-slate-400 mb-6">Select subject for mock test</p>
          <div className="grid grid-cols-2 gap-3">
            {(subjectsList[examType] || subjectsList.tgt).map(s => (
              <button key={s} onClick={() => {
                setSubject(s);
                if (examType === "prt") {
                  // PRT only has kvs pattern, skip directly to mock list
                  setSelectedPattern("kvs");
                  setStep("mock_list");
                  fetchAvailable("kvs", s);
                } else {
                  setStep("pattern_select");
                }
              }}
                className="p-4 bg-slate-900 rounded-xl border border-slate-700 hover:border-blue-500 transition-all text-left"
                data-testid={`pick-subject-${s}`}>
                <BookOpen className="w-5 h-5 text-blue-400 mb-2" />
                <span className="text-sm font-medium">{s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── Pattern Select ────────────────────────────────────────────────
  if (step === "pattern_select") {
    const available = getAvailablePatterns();
    if (available.length === 1 && !selectedPattern) {
      handlePatternSelect(available[0]);
      return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;
    }
    return (
      <div className="min-h-screen bg-slate-950 text-white p-4">
        <div className="max-w-lg mx-auto pt-6">
          <button onClick={() => needsSubjectPick ? setStep("subject_pick") : navigate("/dashboard")} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6" data-testid="mock-back-btn">
            <ArrowLeft className="w-5 h-5" /> {needsSubjectPick ? "Back" : "Dashboard"}
          </button>
          <h1 className="text-2xl font-bold mb-2" data-testid="mock-title">Full Mock Test</h1>
          <p className="text-slate-400 mb-6">Select exam pattern for {examType === "prt" ? "All Subjects (PRT)" : (subjectSelection?.subjectLabel || subject?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "Subject")}</p>

          <button onClick={toggleMedium} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition mb-6" data-testid="change-medium-btn">
            <Globe className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-300">Change Medium</span>
            <Badge className={medium === "en" ? "bg-blue-600 text-white" : "bg-orange-600 text-white"}>{medium === "en" ? "English" : "Hindi"}</Badge>
          </button>

          <div className="space-y-4">
            {available.includes("kvs") && (
              <button onClick={() => handlePatternSelect("kvs")} className="w-full text-left" data-testid="select-kvs-pattern">
                <Card className="bg-slate-900 border-slate-700 hover:border-blue-500 transition-all">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-bold text-white">KVS Pattern</h3>
                        <p className="text-slate-400 text-sm mt-1">70 Questions | 2.5 Hours</p>
                        <div className="flex gap-2 mt-3">
                          <Badge className="bg-blue-900/50 text-blue-300 border-blue-700">60 Objective</Badge>
                          <Badge className="bg-purple-900/50 text-purple-300 border-purple-700">10 Descriptive</Badge>
                        </div>
                        <p className="text-xs text-amber-400 mt-2">-0.25 negative marking for wrong MCQs</p>
                      </div>
                      <ChevronRight className="w-6 h-6 text-slate-500" />
                    </div>
                  </CardContent>
                </Card>
              </button>
            )}
            {available.includes("emrs") && (
              <button onClick={() => handlePatternSelect("emrs")} className="w-full text-left" data-testid="select-emrs-pattern">
                <Card className="bg-slate-900 border-slate-700 hover:border-emerald-500 transition-all">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-bold text-white">EMRS Pattern</h3>
                        <p className="text-slate-400 text-sm mt-1">55 Questions | 3 Hours</p>
                        <div className="flex gap-2 mt-3">
                          <Badge className="bg-blue-900/50 text-blue-300 border-blue-700">40 Objective</Badge>
                          <Badge className="bg-purple-900/50 text-purple-300 border-purple-700">15 Descriptive</Badge>
                        </div>
                        <p className="text-xs text-amber-400 mt-2">-1/3 negative marking for wrong MCQs</p>
                      </div>
                      <ChevronRight className="w-6 h-6 text-slate-500" />
                    </div>
                  </CardContent>
                </Card>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Generation Progress Component ─────────────────────────────────
  const GenerationProgress = () => {
    const { step: phase, objDone, objTotal, descDone, descTotal } = genProgress;
    const totalQ = objTotal + descTotal;
    const totalDone = objDone + descDone;
    const pct = totalQ > 0 ? Math.round((totalDone / totalQ) * 100) : 0;

    let message = "Your questions are being loaded...";
    let subMessage = "";

    if (phase === "loading_questions" || (!phase && objDone < objTotal)) {
      message = `Your questions are being loaded.... (${objDone}/${objTotal})`;
      subMessage = "Creating diverse question types from your syllabus";
    } else if (phase === "preparing_answers" || (objDone >= objTotal * 0.5 && descDone === 0 && objDone < objTotal)) {
      message = `We are preparing your answer sheet.... (${objDone}/${objTotal})`;
      subMessage = "Setting up answer upload section for descriptive questions";
    } else if (phase === "preparing_descriptive") {
      message = `Almost there! Preparing descriptive questions... (${descDone}/${descTotal})`;
      subMessage = "Creating detailed 4-mark questions from your syllabus";
    } else if (phase === "almost_done" || (objDone >= objTotal && descDone > 0)) {
      message = `Please wait, we are almost there.. (${totalDone}/${totalQ})`;
      subMessage = "Final touches on your mock test";
    } else if (phase === "doing_qc") {
      message = "Doing QC...";
      subMessage = "Verifying all answer keys for accuracy";
    } else if (phase === "Complete") {
      message = "Mock Test is Ready!";
      subMessage = "Your mock test is ready to attempt";
    }

    const userEmail = user?.email || user?.mobile || "";

    return (
      <div className="text-center py-8" data-testid="mock-generating">
        <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
          <Sparkles className="w-12 h-12 text-white animate-pulse" />
        </div>
        <h3 className="text-xl font-bold text-white mb-4">{message}</h3>
        <p className="text-slate-400 text-sm mb-4">{subMessage}</p>

        {/* Progress bar */}
        <div className="max-w-sm mx-auto mb-6">
          <div className="w-full bg-slate-800 rounded-full h-3">
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-700"
              style={{ width: `${Math.max(5, pct)}%` }} />
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center">{pct}% complete</p>
        </div>

        {/* Info box */}
        <div className="max-w-sm mx-auto bg-slate-900 border border-slate-700 rounded-xl p-4 mb-4 text-left">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-blue-900/50 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Clock className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">We will make this mock ready within 10 minutes for you</p>
              <p className="text-xs text-slate-400 mt-1">Questions are generated from your LMS syllabus with CBSE pattern evaluation</p>
            </div>
          </div>
          {userEmail && (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-green-900/50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Send className="w-4 h-4 text-green-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">We will send the link to attempt on</p>
                <p className="text-xs text-blue-400 mt-0.5">{userEmail}</p>
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-slate-600">
          You can close this page and come back later. The test will be waiting for you.
        </p>
      </div>
    );
  };

  // ─── Mock List with 10 slots ───────────────────────────────────────
  if (step === "mock_list") {
    const TOTAL_SLOTS = 10;
    const readyMap = {};
    for (const m of availableMocks) readyMap[m.mock_number] = m;

    // Inline progress banner for when generation is running but mocks already exist
    const hasReadyMocks = availableMocks.length > 0;

    return (
      <div className="min-h-screen bg-slate-950 text-white p-4">
        <div className="max-w-lg mx-auto pt-6">
          <button onClick={() => {
            if (pollRef.current) clearInterval(pollRef.current);
            setGenerating(false);
            setGeneratingMockNum(null);
            examType === "prt" ? navigate("/dashboard") : (needsSubjectPick ? setStep("subject_pick") : setStep("pattern_select"));
          }} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6" data-testid="mock-list-back">
            <ArrowLeft className="w-5 h-5" /> Back
          </button>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold" data-testid="mock-list-title">{selectedPattern?.toUpperCase()} Mock Tests</h1>
              <p className="text-slate-400 text-sm">{examType === "prt" ? "All Subjects (PRT)" : (subjectSelection?.subjectLabel || subject?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()))}</p>
            </div>
            <button onClick={toggleMedium} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 transition" data-testid="change-medium-btn">
              <Globe className="w-4 h-4 text-slate-400" />
              <span className="text-xs">{medium === "en" ? "English" : "Hindi"}</span>
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
          ) : (
            <>
              {/* Generation progress — always shown as compact banner at top (never full-screen block) */}
              {generating && (
                <div className="mb-4 p-4 bg-slate-900 border border-blue-800/50 rounded-xl" data-testid="mock-generating-inline">
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-400 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-blue-300 font-medium">
                        {genProgress.step === "doing_qc"
                          ? `Doing QC on Mock ${generatingMockNum}...`
                          : genProgress.step === "Complete"
                            ? `Mock ${generatingMockNum} is ready!`
                            : `Preparing Mock ${generatingMockNum || ""}...`}
                      </p>
                      {genProgress.step && genProgress.step !== "Complete" && (
                        <p className="text-xs text-slate-500 mt-0.5">
                          {genProgress.step === "doing_qc"
                            ? "Verifying answer keys for accuracy..."
                            : genProgress.step === "preparing_descriptive"
                              ? `Descriptive questions: ${genProgress.descDone}/${genProgress.descTotal}`
                              : `Objective questions: ${genProgress.objDone}/${genProgress.objTotal}`}
                        </p>
                      )}
                    </div>
                    {genProgress.step !== "doing_qc" && genProgress.objTotal > 0 && (
                      <span className="text-xs text-slate-400 flex-shrink-0">
                        {Math.round(((genProgress.objDone + genProgress.descDone) / (genProgress.objTotal + genProgress.descTotal || 1)) * 100)}%
                      </span>
                    )}
                  </div>
                  {genProgress.step !== "doing_qc" && genProgress.objTotal > 0 && (
                    <div className="w-full bg-slate-800 rounded-full h-1.5">
                      <div className="bg-blue-500 h-1.5 rounded-full transition-all duration-700"
                        style={{ width: `${Math.max(5, Math.round(((genProgress.objDone + genProgress.descDone) / (genProgress.objTotal + genProgress.descTotal || 1)) * 100))}%` }} />
                    </div>
                  )}
                  {genProgress.step === "doing_qc" && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-green-500 to-cyan-500 rounded-full animate-pulse" style={{ width: "70%" }} />
                      </div>
                      <span className="text-xs text-green-400">QC</span>
                    </div>
                  )}
                </div>
              )}

              {/* Mock list — ALWAYS shown (generation no longer blocks) */}
              {(
                <div className="space-y-3">
                  {Array.from({ length: TOTAL_SLOTS }, (_, i) => i + 1).map((mockNum) => {
                    const mock = readyMap[mockNum];
                    const paused = pausedMocks[mockNum];

                    const isReady = !!mock;
                    const isPaused = !!paused && !isReady;
                    const isThisGenerating = generating && generatingMockNum === mockNum;
                    const isAutoStart = autoStartMock && autoStartMock.mock_number === mockNum;
                    
                    return (
                      <Card key={mockNum} className={`border-slate-700 ${
                        isReady ? "bg-slate-900" : isPaused ? "bg-slate-900 border-amber-800/60" : isThisGenerating ? "bg-slate-900 border-blue-700" : "bg-slate-900/50"
                      } ${isAutoStart ? "ring-2 ring-green-500 ring-offset-2 ring-offset-slate-950" : ""}`} 
                      data-testid={`mock-card-${mockNum}`}>
                        <CardContent className="p-4">
                          {/* Show progress inside the card when generating this specific mock */}
                          {isThisGenerating ? (
                            <div className="space-y-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                  <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                                </div>
                                <div className="flex-1">
                                  <h3 className="font-bold text-blue-300">Full Mock Test {mockNum}</h3>
                                  <p className="text-xs text-blue-400 mt-0.5">Generating... please wait ~10 min</p>
                                </div>
                              </div>

                              {/* Email notification message */}
                              <div className="bg-blue-950/30 border border-blue-800/50 rounded-lg p-3">
                                <div className="flex items-start gap-3">
                                  <Send className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <p className="text-sm font-semibold text-blue-200">We will send you email in 10 minutes when mock test is ready to serve you</p>
                                    {user?.email && <p className="text-xs text-blue-400 mt-1">{user.email}</p>}
                                  </div>
                                </div>
                              </div>

                              {/* Progress bar */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-xs text-slate-300 font-medium">Questions Loaded</p>
                                  <p className="text-xs text-blue-400 font-mono">
                                    {genProgress.objDone + genProgress.descDone} / {genProgress.objTotal + genProgress.descTotal}
                                  </p>
                                </div>
                                <div className="w-full bg-slate-800 rounded-full h-2.5">
                                  <div className="bg-gradient-to-r from-blue-500 to-purple-500 h-2.5 rounded-full transition-all duration-700"
                                    style={{ width: `${Math.max(5, Math.round(((genProgress.objDone + genProgress.descDone) / (genProgress.objTotal + genProgress.descTotal || 1)) * 100))}%` }} />
                                </div>
                                <p className="text-xs text-slate-500 mt-1">
                                  {genProgress.step === "loading_questions" && "Loading objective questions from syllabus..."}
                                  {genProgress.step === "preparing_answers" && "Preparing answer explanations..."}
                                  {genProgress.step === "preparing_descriptive" && "Creating descriptive questions..."}
                                  {genProgress.step === "doing_qc" && "Doing QC — verifying answer keys..."}
                                  {genProgress.step === "Complete" && "Mock test is ready!"}
                                  {!genProgress.step && "Initializing question generation..."}
                                </p>
                              </div>

                              {/* Info note */}
                              <p className="text-xs text-slate-500 text-center">
                                You can close this page and come back later. We'll notify you via email.
                              </p>
                            </div>
                          ) : (
                            /* Normal card view when not generating */
                            <div className="flex flex-col gap-3">
                              <div className="flex items-center gap-3">
                                {isReady ? (
                                  <div className="w-10 h-10 rounded-full bg-blue-900/50 flex items-center justify-center text-blue-300 font-bold text-sm flex-shrink-0">
                                    {mockNum}
                                  </div>
                                ) : isPaused ? (
                                  <div className="w-10 h-10 rounded-full bg-amber-900/50 flex items-center justify-center text-amber-300 font-bold text-sm flex-shrink-0">
                                    {mockNum}
                                  </div>
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-600 flex-shrink-0">
                                    <Lock className="w-4 h-4" />
                                  </div>
                                )}
                                <div className="flex-1">
                                  <h3 className={`font-bold ${isReady ? "text-white" : isPaused ? "text-amber-300" : "text-slate-500"}`}>
                                    Full Mock Test {mockNum}
                                  </h3>
                                  {isReady ? (
                                    <>
                                      <p className="text-xs text-slate-400 mt-0.5">
                                        {mock.total_questions} Qs | {mock.objective_count} Obj + {mock.descriptive_count} Desc | {mock.duration_minutes} min
                                      </p>
                                      <p className="text-xs text-amber-500">{selectedPattern === 'emrs' ? '1/3 Negative Marking' : '1/4 Negative Marking'}</p>
                                      {mock.attempts?.length > 0 && (
                                        <div className="flex gap-2 mt-1 flex-wrap">
                                          {mock.attempts.map((a, idx) => (
                                            <Badge key={idx} className="bg-green-900/40 text-green-300 border-green-700 text-xs">
                                              Attempt {idx + 1}: {a.obtained_marks}/{a.total_marks}
                                            </Badge>
                                          ))}
                                        </div>
                                      )}
                                      {mock.saved_progress && (
                                        <div className="flex items-center gap-1 mt-1">
                                          <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
                                          <span className="text-xs text-yellow-400">
                                            {mock.saved_progress.answered_count} answers saved · {Math.floor(mock.saved_progress.time_left / 60)}m left
                                          </span>
                                        </div>
                                      )}
                                    </>
                                  ) : isPaused ? (
                                    <>
                                      <p className="text-xs text-amber-400 mt-0.5">
                                        Generation incomplete — {paused.obj_count || 0} obj + {paused.desc_count || 0} desc saved ({paused.questions_so_far} total)
                                      </p>
                                      <p className="text-xs text-slate-500">Click Resume to continue generation</p>
                                    </>
                                  ) : (
                                    <p className="text-xs text-slate-600 mt-0.5">Click Load Mock to generate</p>
                                  )}
                                </div>
                                {!isReady && (
                                  <Button onClick={() => handleLoadMock(mockNum)} size="sm"
                                    disabled={generating && generatingMockNum !== mockNum}
                                    className={`${isPaused 
                                      ? "bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                                      : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"} ${generating && generatingMockNum !== mockNum ? "opacity-50 cursor-not-allowed" : ""}`}
                                    data-testid={`load-mock-${mockNum}`}>
                                    {isPaused ? <><RotateCcw className="w-4 h-4 mr-1" /> Resume</> : <><Zap className="w-4 h-4 mr-1" /> Load Mock</>}
                                  </Button>
                                )}
                              </div>
                              {isReady && (
                                <div className="flex items-center gap-2 flex-wrap">
                                  {isAutoStart && (
                                    <Badge className="bg-green-600 text-white text-xs whitespace-nowrap">
                                      From Email
                                    </Badge>
                                  )}
                                  {mock.saved_progress ? (
                                    <Button onClick={() => handleStartTest(mock)} size="sm"
                                      className={`bg-yellow-600 hover:bg-yellow-700 ${isAutoStart ? "ring-2 ring-green-400" : ""}`}
                                      data-testid={`resume-mock-${mockNum}`}>
                                      <Play className="w-4 h-4 mr-1" /> Resume
                                    </Button>
                                  ) : (
                                    <Button onClick={() => handleStartTest(mock)} size="sm"
                                      className={`${mock.attempts?.length > 0 ? "bg-orange-600 hover:bg-orange-700" : "bg-blue-600 hover:bg-blue-700"} ${isAutoStart ? "ring-2 ring-green-400" : ""}`}
                                      data-testid={`start-mock-${mockNum}`}>
                                      {mock.attempts?.length > 0 ? <><RotateCcw className="w-4 h-4 mr-1" />Reattempt</> : "Attempt Test"}
                                    </Button>
                                  )}
                                  {mock.attempts?.length > 0 && (
                                    <Button onClick={() => handleViewSolutionsFromList(mock)} size="sm"
                                      variant="outline"
                                      className="border-indigo-600 text-indigo-400 hover:bg-indigo-900/30"
                                      data-testid={`solutions-mock-${mockNum}`}>
                                      <FileText className="w-4 h-4 mr-1" /> Detailed Solution
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ─── Test Active ───────────────────────────────────────────────────
  if (step === "test_active" && testData && currentQuestion) {
    const isDesc = currentQuestion.type === "descriptive";
    const qNum = currentQuestion.q_num;
    const uploads = descUploads[String(qNum)] || [];
    const mcqType = currentQuestion.mcq_type;

    const getQStatus = (idx) => {
      const q = questions[idx];
      if (!q) return "default";
      if (q.type === "objective") return objAnswers[String(q.q_num)] !== undefined ? "answered" : "default";
      return (descUploads[String(q.q_num)] || []).some(Boolean) ? "answered" : "default";
    };

    const mcqLabels = {
      assertion_reasoning: "Assertion & Reasoning",
      match_the_following: "Sahi Milaan Kare",
      fill_in_blank: "Fill in the Blank",
      statement_based: "Statement Based",
    };

    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col">
        {/* Exit Confirm Dialog */}
        {showExitConfirm && (
          <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center px-4">
            <div className="bg-slate-800 border border-slate-600 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-red-900/40 flex items-center justify-center">
                  <LogOut className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="font-bold text-white">Exit Test?</h3>
                  <p className="text-xs text-slate-400">Your progress will be saved.</p>
                </div>
              </div>
              <p className="text-sm text-slate-300 mb-5">You can resume from where you left off. Your answers and timer will be saved.</p>
              <div className="flex gap-3">
                <button onClick={() => setShowExitConfirm(false)} className="flex-1 py-2.5 rounded-xl border border-slate-600 text-slate-300 text-sm font-medium hover:bg-slate-700 transition">
                  Keep Going
                </button>
                <button onClick={handleExit} className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition" data-testid="confirm-exit-btn">
                  Exit & Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Timer & Progress */}
        <div className="sticky top-0 z-50 bg-slate-900 border-b border-slate-700 px-4 py-3">
          <div className="max-w-2xl mx-auto">
            {/* Top Row: Timer, Pause, Exit */}
            <div className="flex items-center justify-between mb-2">
              <div className={`flex items-center gap-2 font-mono text-lg font-bold ${timeLeft < 300 ? "text-red-400 animate-pulse" : "text-green-400"}`} data-testid="mock-timer">
                <Timer className="w-5 h-5" />{formatTime(timeLeft)}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={toggleMedium} className="px-3 py-1 rounded bg-slate-800 text-xs text-slate-300 hover:bg-slate-700 transition" data-testid="toggle-medium">
                  {medium === "en" ? "हिंदी" : "English"}
                </button>
                <button onClick={handlePause} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-900/40 border border-amber-700/50 text-amber-300 text-xs font-medium hover:bg-amber-900/60 transition" data-testid="pause-test-btn">
                  <PauseCircle className="w-3.5 h-3.5" /> Pause
                </button>
                <button onClick={() => setShowExitConfirm(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-900/30 border border-red-700/40 text-red-400 text-xs font-medium hover:bg-red-900/50 transition" data-testid="exit-test-btn">
                  <LogOut className="w-3.5 h-3.5" /> Exit
                </button>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-3">
                  <Badge className="bg-slate-800 text-slate-300">{selectedPattern?.toUpperCase()}</Badge>
                  <span className="text-slate-400">Q {currentQ + 1}/{questions.length}</span>
                </div>
                <span className="text-slate-400">
                  {questions.filter((_, idx) => getQStatus(idx) === "answered").length}/{questions.length} Answered
                </span>
              </div>
              <Progress 
                value={(questions.filter((_, idx) => getQStatus(idx) === "answered").length / questions.length) * 100} 
                className="h-2 bg-slate-800"
              />
            </div>
          </div>
        </div>

        <div className="flex-1 px-4 py-6 max-w-2xl mx-auto w-full">
          <div className="flex gap-2 mb-3 flex-wrap">
            <Badge className={isDesc ? "bg-purple-900/50 text-purple-300" : "bg-blue-900/50 text-blue-300"}>
            {isDesc ? "Descriptive (4 marks)" : `Objective (1 mark | -${selectedPattern === 'emrs' ? '1/3' : '0.25'} wrong)`}
            </Badge>
            {!isDesc && mcqType && mcqType !== "normal" && (
              <Badge className="bg-amber-900/50 text-amber-300 border-amber-700">{mcqLabels[mcqType] || mcqType}</Badge>
            )}
            {currentQuestion.difficulty && currentQuestion.difficulty !== "moderate" && (
              <Badge className={currentQuestion.difficulty === "hard" ? "bg-red-900/50 text-red-300" : "bg-green-900/50 text-green-300"}>
                {currentQuestion.difficulty}
              </Badge>
            )}
          </div>

          <h2 className="text-lg font-semibold mb-6 leading-relaxed" data-testid="question-text">
            <span className="text-blue-400 mr-2">Q{qNum}.</span>
            {renderQuestionText(currentQuestion)}
          </h2>

          {/* Objective Options */}
          {!isDesc && (
            <div className="space-y-3">
              {((medium === "hi" ? (currentQuestion.options_hi || currentQuestion.options_en) : (currentQuestion.options_en)) || []).map((opt, idx) => {
                const selected = objAnswers[String(qNum)] === idx;
                return (
                  <button key={idx} onClick={() => handleOptionSelect(qNum, idx)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selected ? "border-blue-500 bg-blue-900/30" : "border-slate-700 bg-slate-900 hover:border-slate-500"}`}
                    data-testid={`option-${idx}`}>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full mr-3 text-sm font-bold ${selected ? "bg-blue-600 text-white" : "bg-slate-700 text-slate-300"}`}>
                      {String.fromCharCode(65 + idx)}
                    </span>
                    <MathText text={opt} className="text-slate-200" />
                  </button>
                );
              })}
              {objAnswers[String(qNum)] !== undefined && (
                <button onClick={() => setObjAnswers(prev => { const n = { ...prev }; delete n[String(qNum)]; return n; })}
                  className="text-sm text-slate-500 hover:text-red-400 transition mt-1" data-testid="clear-answer">
                  Clear Selection
                </button>
              )}
            </div>
          )}

          {/* Descriptive Upload */}
          {isDesc && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const qText = (medium === "hi" ? (currentQuestion.question_hi || currentQuestion.question_en) : currentQuestion.question_en) || "";
                    toast.info("Generating PDF...");
                    downloadQuestionSheet(qText).then(() => toast.success("Sheet downloaded!")).catch(() => toast.error("Failed to generate sheet"));
                  }}
                  className="text-teal-300 hover:text-teal-200 hover:bg-teal-900/30 border-teal-700"
                  data-testid="download-sheet-btn"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  Download Sheet
                </Button>
              </div>
              <p className="text-sm text-slate-400">Upload handwritten answer (1 image per question)</p>
              {Array.from({ length: 1 }, (_, i) => i).map((fileIdx) => {
                const ocrKey = `${qNum}_${fileIdx}`;
                const ocrText = extractedTexts[ocrKey];
                const isExtracting = ocrLoading[ocrKey];
                return (
                <div key={fileIdx} className="border-2 border-dashed border-slate-700 rounded-xl p-4">
                  {uploads[fileIdx] ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <img src={uploads[fileIdx]} alt="" className="w-20 h-20 object-cover rounded-lg" />
                        <div className="flex-1">
                          <p className="text-sm text-green-400">Page {fileIdx + 1} uploaded</p>
                          {isExtracting && (
                            <p className="mt-1 flex items-center gap-1.5 text-cyan-300 text-xs"><Loader2 className="w-3 h-3 animate-spin" /> Extracting text...</p>
                          )}
                          {!isExtracting && ocrText && (
                            <button
                              onClick={() => handleOcrPreview(qNum, fileIdx)}
                              className="mt-1 flex items-center gap-1.5 text-slate-500 text-xs hover:text-cyan-300 transition-colors"
                              data-testid={`ocr-retry-btn-${fileIdx}`}
                            >
                              <RotateCcw className="w-3 h-3" /> Re-extract
                            </button>
                          )}
                        </div>
                        <button onClick={() => handleRemoveImage(qNum, fileIdx)} className="text-red-400 hover:text-red-300" data-testid={`remove-img-${fileIdx}`}><Trash2 className="w-5 h-5" /></button>
                      </div>
                      {ocrText && (
                        <div className="bg-slate-800/80 border border-slate-600 rounded-lg p-3" data-testid={`ocr-text-${fileIdx}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <FileText className="w-3.5 h-3.5 text-cyan-400" />
                            <span className="text-xs font-semibold text-cyan-400 uppercase tracking-wide">Extracted Text — Page {fileIdx + 1}</span>
                          </div>
                          <p className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed max-h-32 overflow-y-auto">{ocrText}</p>
                        </div>
                      )}
                      {!isExtracting && ocrText === "" && (
                        <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-2 flex items-center gap-2">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                          <span className="text-xs text-amber-300">No text detected. Try uploading a clearer photo.</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <label className="flex flex-col items-center cursor-pointer py-4" data-testid={`upload-slot-${fileIdx}`}>
                      <Upload className="w-8 h-8 text-slate-500 mb-2" />
                      <span className="text-sm text-slate-400">Upload Page {fileIdx + 1}</span>
                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => handleImageUpload(qNum, fileIdx, e.target.files[0])} />
                    </label>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="sticky bottom-0 bg-slate-900 border-t border-slate-700 px-4 py-3">
          <div className="max-w-2xl mx-auto">
            <div className="flex gap-1 overflow-x-auto pb-3 mb-3 scrollbar-hide">
              {questions.map((q, idx) => {
                const st = getQStatus(idx);
                const isCurrent = idx === currentQ;
                const isDescQ = q.type === "descriptive";
                return (
                  <button key={idx} onClick={() => setCurrentQ(idx)}
                    className={`flex-shrink-0 w-9 h-9 rounded-lg text-xs font-bold transition-all
                      ${isCurrent ? "ring-2 ring-blue-400" : ""}
                      ${st === "answered" ? (isDescQ ? "bg-purple-700 text-white" : "bg-green-700 text-white") : "bg-slate-800 text-slate-400"}
                      ${isDescQ && st !== "answered" ? "border border-purple-700" : ""}
                    `} data-testid={`q-pill-${idx}`}>{q.q_num}</button>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              {/* Prev */}
              <Button onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0}
                variant="outline" className="border-slate-600 text-slate-300 px-3" data-testid="prev-q-btn">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              {/* Submit — compact, centered */}
              <Button onClick={handleSubmit}
                className="px-4 py-2 text-sm font-semibold bg-green-700 hover:bg-green-600 border border-green-600 text-white shrink-0"
                data-testid="submit-test-btn">
                <Send className="w-3.5 h-3.5 mr-1.5" /> Submit
              </Button>
              {/* Next — larger, primary */}
              <Button onClick={() => setCurrentQ(Math.min(questions.length - 1, currentQ + 1))} disabled={currentQ >= questions.length - 1}
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-semibold text-base py-2.5" data-testid="next-q-btn">
                Next <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Submitting ────────────────────────────────────────────────────
  if (step === "submitting") {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center px-6 max-w-sm">
          <Loader2 className="w-16 h-16 animate-spin text-blue-500 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-white mb-2">Evaluating Your Answers...</h2>
          <p className="text-slate-400 text-sm mb-4">Scoring with {selectedPattern === 'emrs' ? '1/3' : '1/4'} negative marking for MCQs</p>
          <div className="bg-slate-800 rounded-xl p-4 text-left space-y-2">
            <p className="text-xs text-slate-400 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" /> Objective answers scored
            </p>
            <p className="text-xs text-slate-400 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin flex-shrink-0" /> AI grading descriptive answers...
            </p>
          </div>
          <p className="text-slate-600 text-xs mt-4">Please keep this screen open. This takes 1-2 minutes.</p>
        </div>
      </div>
    );
  }

  // ─── Result ────────────────────────────────────────────────────────
  if (step === "result" && result) {
    const pct = result.percentage || 0;
    const negMarks = result.negative_marks || 0;
    return (
      <div className="min-h-screen bg-slate-950 text-white p-4">
        <div className="max-w-lg mx-auto pt-6">
          <div className="text-center mb-8">
            <div className={`w-28 h-28 rounded-full mx-auto flex items-center justify-center text-3xl font-bold border-4 ${pct >= 60 ? "border-green-500 text-green-400" : pct >= 40 ? "border-yellow-500 text-yellow-400" : "border-red-500 text-red-400"}`} data-testid="score-circle">{pct}%</div>
            <h1 className="text-2xl font-bold mt-4" data-testid="result-title">Mock Test Result</h1>
            <p className="text-slate-400">{selectedPattern?.toUpperCase()} | Mock {selectedMock?.mock_number}</p>
          </div>

          {/* Percentile & Rank */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Card className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 border-blue-700">
              <CardContent className="p-3 text-center">
                <Medal className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
                <p className="text-xl font-bold text-yellow-400" data-testid="result-rank">#{result.rank || result.total_attempts || 1}</p>
                <p className="text-xs text-slate-400">Rank</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 border-purple-700">
              <CardContent className="p-3 text-center">
                <TrendingUp className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                <p className="text-xl font-bold text-purple-400" data-testid="result-percentile">{result.percentile || 0}%</p>
                <p className="text-xs text-slate-400">Percentile</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-slate-800/50 to-slate-700/30 border-slate-600">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold text-slate-300" data-testid="result-total-attempts">{result.total_attempts || 1}</p>
                <p className="text-xs text-slate-400">Total Attempts</p>
              </CardContent>
            </Card>
          </div>

          {/* Score Summary */}
          <Card className="bg-slate-900 border-slate-700 mb-4" data-testid="score-summary">
            <CardContent className="p-5 space-y-4">
              <div className="flex justify-between items-center text-lg font-bold">
                <span>Your Marks</span>
                <span className="text-blue-400">{result.obtained_marks} / {result.total_marks}</span>
              </div>
              <div className="h-px bg-slate-700" />

              <h3 className="font-semibold text-slate-300">Objective Questions</h3>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="bg-green-900/30 rounded-lg p-2">
                  <p className="text-xl font-bold text-green-400" data-testid="obj-correct">{result.objective_correct}</p>
                  <p className="text-xs text-slate-400">Correct</p>
                </div>
                <div className="bg-red-900/30 rounded-lg p-2">
                  <p className="text-xl font-bold text-red-400" data-testid="obj-wrong">{result.objective_wrong}</p>
                  <p className="text-xs text-slate-400">Wrong</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-2">
                  <p className="text-xl font-bold text-slate-400" data-testid="obj-skipped">{result.objective_not_attempted}</p>
                  <p className="text-xs text-slate-400">Skipped</p>
                </div>
                <div className="bg-amber-900/30 rounded-lg p-2">
                  <p className="text-xl font-bold text-amber-400" data-testid="obj-negative">-{negMarks}</p>
                  <p className="text-xs text-slate-400">Negative</p>
                </div>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Objective Score (after negative)</span>
                <span className="font-bold text-blue-300">{result.objective_marks}</span>
              </div>

              <div className="h-px bg-slate-700" />
              <h3 className="font-semibold text-slate-300">Descriptive (4 marks each)</h3>
              <div className="flex justify-between">
                <span className="text-slate-400">Score</span>
                <span className="font-bold text-purple-400" data-testid="desc-score">{result.descriptive_obtained} / {result.descriptive_total_marks}</span>
              </div>
              {result.descriptive_details?.map((d) => (
                <div key={d.q_num} className="flex justify-between text-sm">
                  <span className="text-slate-500">Q{d.q_num}</span>
                  <span className={d.marks_obtained >= 3 ? "text-green-400" : d.marks_obtained >= 1 ? "text-yellow-400" : "text-red-400"}>
                    {d.marks_obtained}/4 {d.status === "not_attempted" && "(Skipped)"}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="space-y-3">
            <Button 
              onClick={fetchLeaderboard}
              disabled={loadingLeaderboard}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-6 text-lg font-semibold shadow-lg"
            >
              {loadingLeaderboard ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading...</>
              ) : (
                <><Trophy className="w-5 h-5 mr-2" /> View Leaderboard</>
              )}
            </Button>
            <Button onClick={handleShowSolutions} className="w-full bg-indigo-600 hover:bg-indigo-700 py-6 text-lg" data-testid="show-solutions-btn">
              <Eye className="w-5 h-5 mr-2" /> Detailed Solutions
            </Button>
            <Button onClick={handleReattempt} variant="outline" className="w-full border-slate-600 text-slate-300 py-6 text-lg" data-testid="reattempt-btn">
              <RotateCcw className="w-5 h-5 mr-2" /> Back to Mock Tests
            </Button>
            <Button onClick={() => navigate("/dashboard")} variant="ghost" className="w-full text-slate-400"><Home className="w-5 h-5 mr-2" /> Dashboard</Button>
          </div>

          {/* Leaderboard Modal */}
          {showLeaderboard && leaderboardData && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setShowLeaderboard(false)}>
              <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700" onClick={(e) => e.stopPropagation()}>
                <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-6 sticky top-0 z-10">
                  <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    <Trophy className="w-6 h-6" />
                    Leaderboard - Top Performers
                  </h2>
                  <p className="text-sm text-white/90 mt-1">
                    Mock Test {selectedMock?.mock_number} • {leaderboardData.total_participants} participants
                  </p>
                </div>
                <CardContent className="p-6">
                  {/* Top 10 */}
                  <div className="space-y-3 mb-6">
                    {leaderboardData.leaderboard.slice(0, 10).map((entry, idx) => (
                      <div 
                        key={entry.user_id}
                        className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                          entry.user_id === user.id 
                            ? 'bg-blue-900/50 border-blue-500 shadow-md' 
                            : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        {/* Rank */}
                        <div className="flex-shrink-0 w-12 text-center">
                          {idx === 0 && <span className="text-3xl">🥇</span>}
                          {idx === 1 && <span className="text-3xl">🥈</span>}
                          {idx === 2 && <span className="text-3xl">🥉</span>}
                          {idx > 2 && (
                            <div className="text-xl font-bold text-slate-300">#{entry.rank}</div>
                          )}
                        </div>

                        {/* User Info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-white truncate">
                            {entry.user_name}
                            {entry.user_id === user.id && (
                              <Badge className="ml-2 bg-blue-500 text-white text-xs">You</Badge>
                            )}
                          </p>
                          <div className="flex items-center gap-3 text-sm text-slate-400 mt-1">
                            <span className="flex items-center gap-1">
                              <Target className="w-3 h-3" /> {entry.percentage}%
                            </span>
                            <span className="flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> {entry.objective_correct} correct
                            </span>
                          </div>
                        </div>

                        {/* Score */}
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-400">{entry.obtained_marks}</div>
                          <div className="text-xs text-slate-500">/ {entry.total_marks}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* User's Position (if rank > 10) */}
                  {result && result.rank > 10 && (
                    <div className="border-t-2 border-slate-700 pt-4">
                      <p className="text-sm text-slate-400 font-medium mb-3 text-center">Your Position</p>
                      <div className="flex items-center gap-3 p-4 rounded-lg bg-blue-900/50 border-2 border-blue-500 shadow-md">
                        <div className="flex-shrink-0 w-12 text-center">
                          <div className="text-xl font-bold text-blue-400">#{result.rank}</div>
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-white">{user.name}</p>
                          <div className="flex items-center gap-3 text-sm text-slate-400 mt-1">
                            <span>{((result.obtained_marks / result.total_marks) * 100).toFixed(1)}%</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-400">{result.obtained_marks}</div>
                          <div className="text-xs text-slate-500">/ {result.total_marks}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Percentile Distribution */}
                  <div className="mt-6 p-4 bg-gradient-to-r from-purple-900/50 to-blue-900/50 rounded-lg border border-purple-700">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Your Performance
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300">Percentile</span>
                        <span className="font-bold text-purple-400">{result?.percentile || 0}%</span>
                      </div>
                      <Progress value={result?.percentile || 0} className="h-3" />
                      <p className="text-xs text-slate-400 text-center mt-2">
                        You performed better than {result?.percentile || 0}% of participants
                      </p>
                    </div>
                  </div>

                  <Button onClick={() => setShowLeaderboard(false)} className="w-full mt-6 bg-slate-700 hover:bg-slate-600">
                    Close
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── Solutions ─────────────────────────────────────────────────────
  if (step === "solutions" && solutions) {
    const cameFromResult = !!result && !!testData;
    return (
      <div className="min-h-screen bg-slate-950 text-white p-4">
        <div className="max-w-2xl mx-auto pt-6">
          <button onClick={() => cameFromResult ? setStep("result") : handleReattempt()} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6" data-testid="solutions-back">
            <ArrowLeft className="w-5 h-5" /> {cameFromResult ? "Back to Result" : "Back to Mock Tests"}
          </button>
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">Detailed Solutions</h1>
            <button onClick={toggleMedium} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700" data-testid="sol-change-medium">
              <Globe className="w-4 h-4 text-slate-400" />
              <span className="text-xs">{medium === "en" ? "English" : "Hindi"}</span>
            </button>
          </div>

          <div className="space-y-4">
            {solutions.map((sol) => {
              if (sol.type === "objective") {
                const opts = medium === "hi" ? (sol.options_hi || sol.options_en) : sol.options_en;
                const explanation = medium === "hi" ? (sol.explanation_hi || sol.explanation_en) : sol.explanation_en;
                const studentAns = sol.student_answer;
                const isCorrect = studentAns === sol.correct_answer;
                const notAttempted = studentAns === undefined || studentAns === null;
                const mcqTypeLabel = { assertion_reasoning: "Assertion & Reasoning", match_the_following: "Sahi Milaan Kare", fill_in_blank: "Fill in the Blank", statement_based: "Statement Based" }[sol.mcq_type];
                return (
                  <Card key={sol.q_num} className={`border-slate-700 ${isCorrect ? "bg-green-950/30" : notAttempted ? "bg-slate-900" : "bg-red-950/30"}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-2 mb-3 flex-wrap">
                        <Badge className="bg-blue-900/50 text-blue-300 flex-shrink-0">Q{sol.q_num}</Badge>
                        {mcqTypeLabel && <Badge className="bg-amber-900/40 text-amber-300 text-xs">{mcqTypeLabel}</Badge>}
                        {isCorrect ? <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" /> :
                          notAttempted ? <AlertTriangle className="w-5 h-5 text-slate-500 flex-shrink-0" /> :
                          <><XCircle className="w-5 h-5 text-red-500 flex-shrink-0" /><span className="text-xs text-red-400">(-{selectedPattern === 'emrs' ? '1/3' : '0.25'})</span></>}
                      </div>
                      <div className="text-sm text-slate-200 mb-3">{renderQuestionText(sol)}</div>

                      {/* Your Choice label */}
                      {!notAttempted && (
                        <div className={`flex items-center gap-2 mb-2 px-3 py-2 rounded-lg border ${isCorrect ? "bg-green-900/30 border-green-700/50" : "bg-red-900/30 border-red-700/50"}`} data-testid={`your-choice-${sol.q_num}`}>
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Your Choice:</span>
                          <span className={`text-sm font-bold ${isCorrect ? "text-green-300" : "text-red-300"}`}>
                            {String.fromCharCode(65 + studentAns)}. <MathText text={(opts || [])[studentAns] || ""} />
                          </span>
                          {isCorrect ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                        </div>
                      )}
                      {notAttempted && (
                        <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg border bg-slate-800/50 border-slate-600" data-testid={`your-choice-${sol.q_num}`}>
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Your Choice:</span>
                          <span className="text-sm text-slate-500 italic">Not Attempted</span>
                        </div>
                      )}

                      <div className="space-y-1">
                        {opts?.map((opt, idx) => (
                          <div key={idx} className={`text-sm px-3 py-1.5 rounded flex items-center gap-2 ${idx === sol.correct_answer ? "bg-green-900/40 text-green-300 font-semibold" : idx === studentAns && !isCorrect ? "bg-red-900/40 text-red-300 line-through opacity-80" : "text-slate-400"}`}>
                            <span>{String.fromCharCode(65 + idx)}.</span> <MathText text={opt} />
                            {idx === sol.correct_answer && <CheckCircle2 className="w-3.5 h-3.5 text-green-400 flex-shrink-0 ml-auto" />}
                            {idx === studentAns && !isCorrect && <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 ml-auto" />}
                          </div>
                        ))}
                      </div>
                      {explanation && <p className="text-xs text-slate-500 mt-2 italic">Explanation: <MathText text={explanation} /></p>}
                    </CardContent>
                  </Card>
                );
              }
              const idealAns = medium === "hi" ? (sol.ideal_answer_hi || sol.ideal_answer_en) : sol.ideal_answer_en;
              const studentImages = sol.student_images || [];
              const studentExtractedText = sol.extracted_text || "";
              return (
                <Card key={sol.q_num} className="bg-slate-900 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-2 mb-3">
                      <Badge className="bg-purple-900/50 text-purple-300">Q{sol.q_num} (4 marks)</Badge>
                      {sol.marks_obtained !== undefined && (
                        <Badge className={sol.marks_obtained >= 3 ? "bg-green-900/50 text-green-300" : sol.marks_obtained >= 1 ? "bg-yellow-900/50 text-yellow-300" : "bg-red-900/50 text-red-300"}>
                          {sol.marks_obtained}/4
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-slate-200 mb-3">{renderQuestionText(sol)}</div>

                    {/* Your Answer section */}
                    <div className="mb-3 rounded-xl overflow-hidden border border-blue-800/30" data-testid={`your-answer-${sol.q_num}`}>
                      <div className="bg-blue-900/30 px-4 py-2 flex items-center gap-2 border-b border-blue-800/30">
                        <FileText className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-bold text-blue-300 uppercase tracking-wide">Your Answer</span>
                      </div>
                      <div className="p-3 bg-slate-800/40">
                        {studentImages.length > 0 ? (
                          <div className="space-y-2">
                            <div className="flex gap-2 flex-wrap">
                              {studentImages.filter(Boolean).map((img, i) => (
                                <img key={i} src={img} alt={`Page ${i+1}`} className="w-16 h-16 object-cover rounded-lg border border-slate-600" />
                              ))}
                            </div>
                            {studentExtractedText && (
                              <div className="bg-slate-900/60 rounded-lg p-3 border border-slate-700">
                                <p className="text-xs font-semibold text-cyan-400 mb-1 uppercase tracking-wide">Extracted Text</p>
                                <p className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed">{studentExtractedText}</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500 italic">Not Attempted</p>
                        )}
                      </div>
                    </div>

                    {/* AI Feedback with highlights */}
                    {sol.feedback && (
                      <div className="mb-3">
                        <DescriptiveFeedback feedback={sol.feedback} isDark={true} />
                      </div>
                    )}
                    <IdealAnswerDisplay text={idealAns} isDark={true} />
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-6 space-y-3">
            <Button onClick={handleReattempt} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="reattempt-solutions-btn">
              <RotateCcw className="w-5 h-5 mr-2" /> Back to Mock Tests
            </Button>
            <Button onClick={() => navigate("/dashboard")} variant="ghost" className="w-full text-slate-400"><Home className="w-5 h-5 mr-2" /> Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;
}
